import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Mail, Plus, Send, Calendar, History, Settings, 
  FileText, Clock, CheckCircle, AlertCircle,
  Edit, Trash2, Eye, Play, Users
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CAMPAIGN_TYPES = {
  policy_renewal_reminder: {
    icon: '⏰',
    label: 'Renovación de Póliza',
    color: 'bg-purple-100 text-purple-800'
  },
  receipt_confirmation: {
    icon: '✅',
    label: 'Confirmación de Pago',
    color: 'bg-green-100 text-green-800'
  },
  payment_reminder: {
    icon: '💳',
    label: 'Recordatorio de Pago',
    color: 'bg-red-100 text-red-800'
  },
  birthday_greeting: {
    icon: '🎉',
    label: 'Cumpleaños',
    color: 'bg-yellow-100 text-yellow-800'
  },
  portal_access: {
    icon: '🔐',
    label: 'Acceso al Portal',
    color: 'bg-blue-100 text-blue-800'
  },
  special_announcement: {
    icon: '✨',
    label: 'Comunicación Especial',
    color: 'bg-indigo-100 text-indigo-800'
  }
};

export default function EmailCampaigns() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resendConfig, setResendConfig] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Fetch campaigns, config, and stats in parallel
      const [campaignsRes, configRes, statsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/email-campaigns`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${BACKEND_URL}/api/email-campaigns/config/resend`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${BACKEND_URL}/api/email-campaigns/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setCampaigns(campaignsRes.data);
      setResendConfig(configRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (campaignId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta campaña?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/email-campaigns/${campaignId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Campaña eliminada');
      fetchData();
    } catch (error) {
      console.error('Error deleting campaign:', error);
      toast.error('Error al eliminar campaña');
    }
  };

  const handleToggleActive = async (campaignId, currentStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${BACKEND_URL}/api/email-campaigns/${campaignId}`,
        { is_active: !currentStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(currentStatus ? 'Campaña desactivada' : 'Campaña activada');
      fetchData();
    } catch (error) {
      console.error('Error toggling campaign:', error);
      toast.error('Error al cambiar estado de campaña');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando campañas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Mail className="h-8 w-8 text-indigo-600" />
              Campañas de Email
            </h1>
            <p className="text-gray-600 mt-2">Gestiona tus comunicaciones automatizadas</p>
          </div>
          
          <div className="flex gap-3 flex-wrap">
            <Button
              onClick={() => navigate('/email-campaigns/approval')}
              className="gap-2"
            >
              <CheckCircle className="h-4 w-4" />
              Gestión y Aprobación
            </Button>
            <Button
              onClick={() => navigate('/email-campaigns/messages/review')}
              className="gap-2"
            >
              <Users className="h-4 w-4" />
              Mensajes Individuales
            </Button>
            <Button
              onClick={() => navigate('/email-campaigns/config/system')}
              variant="outline"
              className="gap-2"
            >
              <Settings className="h-4 w-4" />
              Variables del Sistema
            </Button>
            <Button
              onClick={() => navigate('/email-campaigns/config')}
              variant="outline"
              className="gap-2"
            >
              <Settings className="h-4 w-4" />
              Configurar Resend
            </Button>
            <Button
              onClick={() => navigate('/email-campaigns/history')}
              variant="outline"
              className="gap-2"
            >
              <History className="h-4 w-4" />
              Historial
            </Button>
            <Button
              onClick={() => navigate('/email-campaigns/scheduled')}
              variant="outline"
              className="gap-2"
            >
              <Calendar className="h-4 w-4" />
              Programados
            </Button>
          </div>
        </div>
      </div>

      {/* Resend Config Warning */}
      {!resendConfig && (
        <div className="mb-6 p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded-lg">
          <div className="flex items-start">
            <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 mr-3" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-yellow-800">
                Configuración Requerida
              </h3>
              <p className="text-sm text-yellow-700 mt-1">
                Debes configurar tu API Key de Resend antes de enviar emails.
              </p>
              <Button
                onClick={() => navigate('/email-campaigns/config')}
                className="mt-3"
                size="sm"
              >
                Configurar Ahora
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Emails Enviados</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.total_sent}</p>
                </div>
                <div className="p-3 bg-green-100 rounded-lg">
                  <CheckCircle className="h-8 w-8 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Emails Fallidos</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.total_failed}</p>
                </div>
                <div className="p-3 bg-red-100 rounded-lg">
                  <AlertCircle className="h-8 w-8 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Campañas Activas</p>
                  <p className="text-3xl font-bold text-gray-900">
                    {campaigns.filter(c => c.is_active).length}
                  </p>
                </div>
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Mail className="h-8 w-8 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Create New Button */}
      <div className="mb-6">
        <Button
          onClick={() => navigate('/email-campaigns/new')}
          className="w-full sm:w-auto gap-2"
          size="lg"
        >
          <Plus className="h-5 w-5" />
          Crear Nueva Campaña
        </Button>
      </div>

      {/* Campaigns Grid */}
      {campaigns.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Mail className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No hay campañas creadas
            </h3>
            <p className="text-gray-600 mb-6">
              Crea tu primera campaña para comenzar a enviar emails automatizados
            </p>
            <Button onClick={() => navigate('/email-campaigns/new')}>
              Crear Campaña
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {campaigns.map((campaign) => {
            const typeInfo = CAMPAIGN_TYPES[campaign.campaign_type] || {};
            
            return (
              <Card key={campaign.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">{typeInfo.icon}</span>
                      <Badge className={typeInfo.color}>
                        {typeInfo.label}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-600">
                        {campaign.is_active ? 'Activa' : 'Inactiva'}
                      </span>
                      <Switch
                        checked={campaign.is_active}
                        onCheckedChange={() => handleToggleActive(campaign.id, campaign.is_active)}
                      />
                    </div>
                  </div>
                  <CardTitle className="text-lg">{campaign.name}</CardTitle>
                  <CardDescription className="text-sm line-clamp-1">
                    {campaign.subject}
                  </CardDescription>
                </CardHeader>
                
                <CardContent>
                  <div className="text-xs text-gray-500 mb-4">
                    Última modificación: {new Date(campaign.last_modified).toLocaleDateString('es-MX')}
                  </div>

                  <div className="flex gap-2 flex-wrap">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate(`/email-campaigns/${campaign.id}/preview`)}
                      className="flex-1 gap-1"
                    >
                      <Eye className="h-4 w-4" />
                      Preview
                    </Button>
                    
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate(`/email-campaigns/${campaign.id}/edit`)}
                      className="flex-1 gap-1"
                    >
                      <Edit className="h-4 w-4" />
                      Editar
                    </Button>
                  </div>

                  <div className="flex gap-2 mt-2">
                    <Button
                      size="sm"
                      onClick={() => navigate(`/email-campaigns/${campaign.id}/send`)}
                      disabled={!resendConfig}
                      className="flex-1 gap-1"
                    >
                      <Send className="h-4 w-4" />
                      Enviar
                    </Button>
                    
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDelete(campaign.id)}
                      className="gap-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
